/**
 * Gyroscope zero-rate offset, estimated from rest.
 *
 * Copy of capture-app/src/ble/gyroBias.ts, mirrored again in
 * api/app/worker/gyro_bias.py. Three copies because three runtimes; the
 * L2 parity test scores the device replica against the pre-pass and will
 * catch them drifting apart. Change one, change all three.
 *
 * A MEMS gyro reports a small non-zero rate while perfectly still. The
 * BMI160's datasheet puts the typical offset at up to ±3 °/s; measured on
 * one of our boards over a real recording it is much smaller and very
 * stable — x +0.025, y +0.332, z −0.151 °/s, with a p10..p90 spread inside
 * ±0.015 °/s. Small, but it integrates: 0.37 °/s is ~22 °/min of heading
 * drift, and a board nearer the datasheet's ±3 °/s would be ~180 °/min.
 *
 * WHAT IT IS CORRECTED FOR, AND WHAT IT IS NOT
 *
 * The correction is applied to the fusion's INPUT only. The recorded
 * gyroscope stream stays raw, deliberately: the server re-derives fusion
 * from that same raw stream, so if we corrected on the way to disk the two
 * would be correcting different things and parity would quietly break. Raw
 * in the file also means a better estimator later can be applied to
 * recordings already captured.
 *
 * Bias is a DC offset, so it cancels in anything differential — the
 * jerk-based smoothness metric and the 8–12 Hz tremor band are unaffected.
 * What it moves is integrated heading, and the hand-activity threshold.
 *
 * DETECTING REST WITHOUT CIRCULARITY
 *
 * Rest is judged primarily from the ACCELEROMETER: a still hand reads ~1 g
 * with almost no variance. Judging it from the gyro alone would be circular
 * — "the gyro is near zero, therefore its offset is near zero" — and would
 * drag every estimate toward whatever it already believed.
 *
 * A gyro bound is still applied, at a level far above any plausible offset
 * (REST_GYRO_MAX_DPS, 5 °/s vs a ±3 °/s datasheet worst case). That is not
 * circular, it closes a real hole: rotating steadily about the gravity
 * vector keeps the accelerometer perfectly constant, so the accel gate
 * alone would happily call a slow twist "rest" and absorb it into the bias.
 */

/** Window over which rest is judged and the offset averaged — 1 s at 100 Hz. */
export const REST_WINDOW = 100;

/** |mean |a|| may differ from 1 g by at most this (g). Widened from 0.03
 *  on 2026-09-21 — a still board reading 1.0539 g (ordinary MEMS scale
 *  error) produced zero rest windows. Stillness is judged by STABILITY;
 *  this bound only catches free fall and a slow wide circle. */
export const REST_ACCEL_TOL_G = 0.15;

/** Standard deviation of |a| across the window, at most (g). */
export const REST_ACCEL_SD_G = 0.01;

/**
 * Gyro magnitude ceiling for a window to count as rest (°/s).
 *
 * Bounds the search to offsets the hardware can actually have — the
 * BMI160's worst case is ±3 °/s — while excluding real motion. See the
 * gravity-axis rotation hole above for why the accel gate alone is not
 * enough.
 */
export const REST_GYRO_MAX_DPS = 5;

/**
 * How fast the estimate follows a new rest window.
 *
 * Deliberately slow. The offset drifts with temperature over minutes, not
 * seconds, so there is nothing to chase; and a low weight means one
 * misclassified window (a hand resting against something that is itself
 * moving) cannot move the estimate far.
 */
export const BIAS_EMA_ALPHA = 0.1;

export interface GyroBias {
  x: number;
  y: number;
  z: number;
  /** Rest windows folded in so far. 0 ⇒ `x/y/z` are still zero. */
  samples: number;
}

export function zeroBias(): GyroBias {
  return { x: 0, y: 0, z: 0, samples: 0 };
}

function mean(xs: number[]): number {
  let t = 0;
  for (const x of xs) t += x;
  return t / xs.length;
}

/**
 * Fold one window of paired accel+gyro readings into the estimate.
 *
 * Returns the bias unchanged when the window is not rest, which is the
 * common case during a procedure. `accel` is in g, `gyro` in °/s, and the
 * two arrays must be the same length and aligned.
 */
export function updateBias(
  bias: GyroBias,
  accel: readonly (readonly number[])[],
  gyro: readonly (readonly number[])[],
): GyroBias {
  const n = Math.min(accel.length, gyro.length);
  if (n < REST_WINDOW) return bias;

  const mags: number[] = [];
  for (let i = 0; i < n; i += 1) {
    const [x, y, z] = accel[i];
    mags.push(Math.sqrt(x * x + y * y + z * z));
  }
  const m = mean(mags);
  if (Math.abs(m - 1) > REST_ACCEL_TOL_G) return bias;
  let varSum = 0;
  for (const v of mags) varSum += (v - m) * (v - m);
  if (Math.sqrt(varSum / n) > REST_ACCEL_SD_G) return bias;

  const gx: number[] = [];
  const gy: number[] = [];
  const gz: number[] = [];
  for (let i = 0; i < n; i += 1) {
    const [x, y, z] = gyro[i];
    if (Math.sqrt(x * x + y * y + z * z) > REST_GYRO_MAX_DPS) return bias;
    gx.push(x);
    gy.push(y);
    gz.push(z);
  }

  const wx = mean(gx);
  const wy = mean(gy);
  const wz = mean(gz);
  // First rest window seeds the estimate outright — easing in from zero
  // would leave most of the offset uncorrected for several windows, and
  // there is nothing to be cautious about when the prior is "no idea".
  if (bias.samples === 0) return { x: wx, y: wy, z: wz, samples: 1 };
  const a = BIAS_EMA_ALPHA;
  return {
    x: bias.x + a * (wx - bias.x),
    y: bias.y + a * (wy - bias.y),
    z: bias.z + a * (wz - bias.z),
    samples: bias.samples + 1,
  };
}

/** Gyro reading with the estimated offset removed. A no-op until the first
 *  rest window, so a hand that never stops still gets raw gyro. */
export function applyBias(
  bias: GyroBias,
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  if (bias.samples === 0) return [x, y, z];
  return [x - bias.x, y - bias.y, z - bias.z];
}
